'use client'
// =============================================================================
// MissionCard — widget CORE (right-panel) : hiérarchie réelle Mission →
// Branches → Jobs, lue depuis l'API Mission Engine (/api/missions,
// /api/gpu-nodes). Anciennement MissionActivePanel.tsx (src/components/
// missions/) — déplacé ici lors du découpage RightPanel → AI Command Center
// (Slice 2, voir docs/architecture/command-center-ui.md), seul importeur.
// =============================================================================
import { useState, useEffect, useCallback } from 'react'
import { useSession } from 'next-auth/react'

const BLUE  = '#4f7cff'
const CYAN  = '#00d4ff'
const GREEN = '#34d399'
const AMBER = '#f5a623'
const RED   = '#ef4444'
const MUTED = '#475569'

type MissionStatus =
  | 'NEW' | 'ANALYSIS' | 'PLANNING' | 'EXECUTION' | 'VALIDATION'
  | 'COMPLETED' | 'WAITING_USER' | 'FAILED' | 'CANCELLED'

interface MissionLite {
  id: string; objective: string; status: MissionStatus
  parentId: string | null; assignedAgents: string[]
  updatedAt: string; createdAt: string
}

interface MissionDetail extends MissionLite {
  children?: MissionLite[]
  memory?: { log?: { at: string; event: string; detail?: string }[] } | null
}

const ACTIVE_STATUSES: MissionStatus[] = ['NEW', 'ANALYSIS', 'PLANNING', 'EXECUTION', 'VALIDATION', 'WAITING_USER']

const PHASE_LABEL: Record<MissionStatus, string> = {
  NEW: 'Nouvelle', ANALYSIS: 'Analyse', PLANNING: 'Planification',
  EXECUTION: 'Exécution', VALIDATION: 'Validation', COMPLETED: 'Terminée',
  WAITING_USER: 'Attente utilisateur', FAILED: 'Échec', CANCELLED: 'Annulée',
}

const BRANCH_ICON: Record<MissionStatus, string> = {
  COMPLETED: '✓', EXECUTION: '▶', WAITING_USER: '⏸', VALIDATION: '◐',
  FAILED: '✕', CANCELLED: '✕', NEW: '○', ANALYSIS: '○', PLANNING: '○',
}

const BRANCH_COLOR: Record<MissionStatus, string> = {
  COMPLETED: GREEN, EXECUTION: CYAN, WAITING_USER: AMBER, VALIDATION: BLUE,
  FAILED: RED, CANCELLED: MUTED, NEW: MUTED, ANALYSIS: MUTED, PLANNING: MUTED,
}

function fmtAgo(iso?: string | null): string {
  if (!iso) return '—'
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (s < 60) return `il y a ${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `il y a ${m} min`
  return `il y a ${Math.round(m / 60)} h`
}

interface GpuNodeLite {
  id: string; hostname: string; currentMissionId?: string | null
}

export function MissionCard() {
  const { data: session } = useSession()
  const [missions, setMissions] = useState<MissionLite[] | null>(null)
  const [current, setCurrent]   = useState<MissionDetail | null>(null)
  const [nodes, setNodes]       = useState<GpuNodeLite[]>([])
  const [, setTick]             = useState(0) // force re-render pour rafraîchir "il y a Ns"

  const refresh = useCallback(() => {
    fetch('/api/missions?limit=500')
      .then(r => r.ok ? r.json() : [])
      .then(d => setMissions(Array.isArray(d) ? d : []))
      .catch(() => {})
    fetch('/api/gpu-nodes')
      .then(r => r.ok ? r.json() : null)
      .then(d => setNodes(Array.isArray(d?.nodes) ? d.nodes : []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!session) return
    refresh()
    const iv = setInterval(refresh, 15_000)
    return () => clearInterval(iv)
  }, [session, refresh])

  // Horloge locale pour "il y a Ns" — ne refait pas d'appel réseau.
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(iv)
  }, [])

  const active = (missions ?? [])
    .filter(m => ACTIVE_STATUSES.includes(m.status))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
  const rootCandidate = active.find(m => !m.parentId) ?? active[0] ?? null

  useEffect(() => {
    if (!rootCandidate) { setCurrent(null); return }
    fetch(`/api/missions/${rootCandidate.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(setCurrent)
      .catch(() => {})
  }, [rootCandidate?.id])

  if (!session) return null

  if (missions === null) {
    return (
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'rgba(255,255,255,0.2)', padding: '8px 2px' }}>
        ⟳ Chargement…
      </div>
    )
  }

  const rootMissions  = missions.filter(m => !m.parentId)
  const doneRoot       = rootMissions.filter(m => m.status === 'COMPLETED').length

  if (!current) {
    return (
      <div style={{
        padding: '12px 14px', borderRadius: 8,
        background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
        marginBottom: 10, fontFamily: 'var(--font-mono)',
      }}>
        <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
          MISSION ACTIVE
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
          Aucune mission en cours — Skynet est prêt.
        </div>
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', marginTop: 4 }}>
          {doneRoot}/{rootMissions.length} missions complétées
        </div>
      </div>
    )
  }

  const children      = current.children ?? []
  const doneChildren  = children.filter(c => c.status === 'COMPLETED').length
  const lastLog        = current.memory?.log?.[current.memory.log.length - 1]
  const lastActivity   = lastLog?.at ?? current.updatedAt
  const executingJobs  = active.filter(m => m.status === 'EXECUTION' && (m.parentId === current.id || m.id === current.id))

  return (
    <div style={{ marginBottom: 10 }}>
      {/* Carte mission */}
      <div style={{
        padding: '12px 14px', borderRadius: 8,
        background: 'rgba(79,124,255,0.04)', border: '1px solid rgba(79,124,255,0.12)',
        marginBottom: 8, fontFamily: 'var(--font-mono)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>
            🚀 MISSION ACTIVE
          </span>
          <span style={{
            fontSize: 8, color: BRANCH_COLOR[current.status], display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: '50%', background: BRANCH_COLOR[current.status],
              display: 'inline-block', animation: 'pulse-dot 1.5s infinite',
            }} />
            {PHASE_LABEL[current.status]}
          </span>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600, marginBottom: 8 }}>
          {current.objective}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 9 }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.2)', marginBottom: 2 }}>Worker actif</div>
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>
              {current.assignedAgents.join(', ') || '—'}
            </div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.2)', marginBottom: 2 }}>Progression</div>
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>
              {doneRoot} / {rootMissions.length} missions
            </div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.2)', marginBottom: 2 }}>Sous-tâches</div>
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>
              {children.length > 0 ? `${doneChildren} / ${children.length}` : '—'}
            </div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.2)', marginBottom: 2 }}>Dernière activité</div>
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>{fmtAgo(lastActivity)}</div>
          </div>
        </div>
      </div>

      {/* Branches (sous-missions) */}
      {children.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.2)', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>
            BRANCHES
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {children.map(c => (
              <div key={c.id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
                borderRadius: 6, background: `${BRANCH_COLOR[c.status]}08`,
                fontFamily: 'var(--font-mono)', fontSize: 10.5,
              }}>
                <span style={{ color: BRANCH_COLOR[c.status], flexShrink: 0 }}>{BRANCH_ICON[c.status]}</span>
                <span style={{ flex: 1, color: 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.objective}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Jobs (missions en exécution rattachées) */}
      <div>
        <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.2)', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>
          JOBS
        </div>
        {executingJobs.length === 0 ? (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)' }}>
            Aucun job en exécution
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {executingJobs.map(j => {
              // Nœud réellement en train de traiter cette mission (posé par
              // dispatchGpuTask) — absent si repli Ollama local (pas tracké).
              const worker = nodes.find(n => n.currentMissionId === j.id)
              return (
                <div key={j.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
                  borderRadius: 6, background: `${CYAN}07`, fontFamily: 'var(--font-mono)', fontSize: 10.5,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: CYAN, boxShadow: `0 0 6px ${CYAN}`, animation: 'pulse-dot 1.4s infinite', flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {worker && <span style={{ color: CYAN }}>🖥 {worker.hostname} · </span>}
                    <span style={{ color: 'rgba(255,255,255,0.55)' }}>{j.assignedAgents.join(', ') || j.objective}</span>
                  </span>
                  <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>{fmtAgo(j.updatedAt)}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
